import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { BehaviorSubject, Observable } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

import { EnumSelect } from '@summit/core';
import { isNumber, SummitIdObject, ViewField } from '@summit/models';

import { SummitControl } from '../../models';
import { SFormAutoCompleteComponent } from '../s-form-autocomplete/s-form-autocomplete.component';
import { SFormInputSelectComponent } from '../s-form-input-select';

@Component({
  selector: 's-form-input-enum',
  templateUrl: './s-form-input-enum.component.html',
  styleUrls: ['./s-form-input-enum.component.scss'],
  standalone: true,
  imports: [SFormInputSelectComponent, ReactiveFormsModule, SFormAutoCompleteComponent]
  //changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormInputEnumComponent<T extends SummitIdObject> extends SummitControl<T> implements OnInit {
  debugging = false;
  @Input() id: string; //needed for file upload
  @Input() nonFormValue: unknown;
  @Input() filter: T[] | number[];
  @Input() filter$: Observable<string[]>;
  @Input() enumObject: number[];
  @Input() allowZero = false;
  @Input() sort = false;
  @Input() mapping: Record<any, string>;
  @Output() triggerChange: EventEmitter<any> = new EventEmitter<any>();
  @Input() defaultText: string;
  @Input() multiple: boolean;
  @Input() searchable: boolean = false;

  items = new BehaviorSubject<Array<EnumSelect>>([]);
  items$: Observable<Array<EnumSelect>> = this.items.asObservable();
  itemsForMulti$: Observable<SummitIdObject[]> = this.items$.pipe(
    map((items) => items.map((item) => ({ id: item.id.toString(), name: item.value }) as SummitIdObject))
  );
  property?: ViewField<T>;

  constructor() {
    super();
  }

  override ngOnInit() {
    this.showClear ??= false;
    const fieldName = this.fieldName;
    //if (this.fieldName == 'type') this.debugging = true;
    if (this.debugging) console.log(`Displaying: ${this.fieldName}`);

    if (fieldName != null) {
      const control = this.form?.controls[fieldName];
      if (control) {
        if (this.debugging) console.log(`Number: ${control.value}`, isNumber(control.value));
        control.valueChanges.pipe(startWith(control.value), takeUntil(this.disposing$)).subscribe((value) => {
          if (isNumber(value)) {
            control.setValue(value.toString());
            if (this.debugging) console.log(`Converted: ${this.fieldName} to string`, control.value);
          } else if (Array.isArray(value) && value.length > 0 && value.every((s) => isNumber(s))) {
            control.setValue(value.map((x) => x.toString()));
            if (this.debugging) console.log(`Converted: ${this.fieldName} to string`, control.value);
          }
        });
      } else {
        console.warn(`Could not find ${this.fieldName?.toString()} in form`);
      }
    }
    if (this.schema && fieldName) {
      this.property = this.schema.getAttribute(fieldName as keyof T & string, this.groupName);
      this.nullableLabel ??= this.property?.displayNullText ?? '';
      this.displayHint ??= this.property?.displayHint ?? '';
      if (this.debugging) console.log(`${this.fieldName} property = `, this.property);
      if (!this.property) {
        console.error(`Could not find Property ${this.fieldName} on branch ${this.groupName} on tree:`, this.schema);
      } else {
        if (this.debugging)
          console.log(`Selecting between ${this.label} with fallback to ${this.property.displayName}`);
        this.label = this.label ? this.label : this.property.displayName;
        if (!this.mapping) {
          if (this.debugging) console.log(`Pulled Mapping from property, ${this.property.displayTypeParameter}`);
          if (this.debugging) console.log(`Property, ${this.property}`);
          this.mapping = this.property.displayTypeParameter as Record<any, string>;
          if (this.debugging) console.log(`new Mapping:`, this.mapping);
        }
      }
    }
    if (this.debugging) console.log(`${this.fieldName}:Allow Zero = ${this.allowZero} for ${this.fieldName}`);
    if (this.enumObject) {
      this.items.value.length = 0;
      if (this.mapping) {
        if (this.debugging) console.log(`${this.fieldName}:Using Mapping`, this.mapping, this.enumObject);
        this.enumObject
          .filter((element) => this.filter == null || this.filter.findIndex((item) => item == element) < 0)
          .forEach((element) => {
            if (this.debugging) console.log(`Adding ${element}: ${this.mapping[element]}`);
            this.items.value.push(
              new EnumSelect({
                id: element.toString(),
                value: this.mapping[element]
              })
            );
          });
      } else {
        this.enumObject
          .filter((element) => this.filter == null || this.filter.findIndex((item) => item == element) < 0)
          .forEach((element) => {
            this.items.value.push(new EnumSelect({ id: element.toString(), value: element.toString() }));
          });
      }
      if (this.allowZero === true) {
        const zeroIndex = this.items.value.findIndex((entry) => entry?.id == 0);
        if (zeroIndex < 0) {
          //console.log(`${this.fieldName}: Adding Zero = ${this.enumObjectMap[0]}`);
          this.items.value.unshift(new EnumSelect({ id: '0', value: this.mapping[0] }));
        }
      }
      if (this.nullableLabel) {
        this.items.value.unshift(new EnumSelect({ id: '', value: this.nullableLabel }));
        const control = fieldName ? this.form?.controls[fieldName] : undefined;
        control?.valueChanges.pipe(startWith(control.value), takeUntil(this.disposing$)).subscribe((value) => {
          //console.log(`${this.fieldName}: Value Changed to ${value}`, value);
          if (value == null || value == undefined) {
            control.setValue('');
          }
        });
      }
      this.publish(this.items.value);
    } else {
      console.error(`No Enum Object provided for Field: ${this.fieldName}`);
    }
    //console.log(`${this.fieldName}: ENUM Select List: `, this.items.value);
  }

  publish(data: EnumSelect[]) {
    if (this.debugging) console.log(`Displaying: `, data);
    //console.log("DATA BEFORE",data);
    if (this.sort) {
      /*
      console.log("DATA AFTER",data.sort((a, b) => {
        if (a.value > b.value) return 1;
        if (a.value < b.value) return -1;
        return 0;}));
*/
      this.items.next(
        data.sort((a, b) => {
          if (a.value > b.value) return 1;
          if (a.value < b.value) return -1;
          return 0;
        })
      );
    } else {
      this.items.next(data);
    }
  }

  // getObject(): any {
  //   console.log('test', this.getName());
  //   const value = this.form.controls[this.fieldName.toString()]?.value;
  //   if (value != null && value != undefined) {
  //     console.log('testing for value', value);
  //     // if (SDuration.isDuration(value)) {
  //     //   return value;
  //     // }
  //     return value;
  //   }
  //   return null;
  // }
}
